import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FiUsers, FiZap, FiCheckCircle, FiCpu, FiTrendingUp, FiSettings } from "react-icons/fi";

const WhyChooseUs = () => {
  const { t } = useTranslation();

  const cards = [
    {
      icon: FiUsers,
      titleKey: "why_team_title",
      defaultTitle: "Experienced Team",
      descKey: "why_team_desc",
      defaultDesc: "A robust division of creators, engineers, and specialists carrying years of domain expertise.",
    },
    {
      icon: FiZap,
      titleKey: "why_delivery_title",
      defaultTitle: "Fast Delivery",
      descKey: "why_delivery_desc",
      defaultDesc: "Optimized sprint architectures and automated pipelines ensuring rapid, stable releases.",
    },
    {
      icon: FiCheckCircle,
      titleKey: "why_qa_title",
      defaultTitle: "Quality Assurance",
      descKey: "why_qa_desc",
      defaultDesc: "Comprehensive code reviews, automated unit tests, and rigorous validation audits.",
    },
    {
      icon: FiCpu,
      titleKey: "why_tech_title",
      defaultTitle: "Latest Technologies",
      descKey: "why_tech_desc",
      defaultDesc: "Continuous tech R&D adopting cutting-edge tools to future-proof your digital framework.",
    },
    {
      icon: FiSettings,
      titleKey: "why_support_title",
      defaultTitle: "Long-term Support",
      descKey: "why_support_desc",
      defaultDesc: "Ongoing maintenance, optimization, SLA support, and scaling guidance post-launch.",
    },
    {
      icon: FiTrendingUp,
      titleKey: "why_cost_title",
      defaultTitle: "Cost-effective Solutions",
      descKey: "why_cost_desc",
      defaultDesc: "Transparent pricing models, lean operations, and high returns on engineering investments.",
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-white dark:bg-[#080808] transition-colors duration-500 overflow-hidden relative">
      {/* Background gradients */}
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-indigo-500/5 dark:bg-purple-500/5 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-xs font-bold text-blue-600 dark:text-purple-400 uppercase tracking-widest">
            {t('whyChooseUsLabel', 'Our Advantages')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight">
            {t('whyChooseUsTitle', 'Why Choose Softkel')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-purple-500 dark:to-pink-500 mx-auto mt-4 rounded-full" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {cards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ 
                y: -8,
                transition: { duration: 0.2 }
              }}
              className="group relative p-6 sm:p-8 rounded-3xl bg-white dark:bg-[#111111] border border-slate-200/60 dark:border-neutral-800/60 shadow-sm hover:shadow-2xl hover:shadow-blue-500/10 dark:hover:shadow-purple-500/10 hover:border-blue-500/30 dark:hover:border-purple-500/30 overflow-hidden transition-all duration-300"
            >
              {/* Subtle hover background highlight */}
              <div className="absolute -inset-px bg-gradient-to-br from-blue-500/5 to-purple-500/5 dark:from-purple-500/5 dark:to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl" />
              
              {/* Icon Container */}
              <div className="relative z-10 w-12 h-12 flex items-center justify-center rounded-2xl bg-blue-50 dark:bg-neutral-900 text-blue-600 dark:text-purple-400 group-hover:scale-110 group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-indigo-600 dark:group-hover:from-purple-500 dark:group-hover:to-pink-500 group-hover:text-white dark:group-hover:text-white transition-all duration-300 mb-6">
                <card.icon className="w-6 h-6" />
              </div>

              {/* Title */}
              <h3 className="relative z-10 text-lg font-extrabold text-slate-900 dark:text-white mb-3">
                {t(card.titleKey, card.defaultTitle)}
              </h3>

              {/* Description */}
              <p className="relative z-10 text-sm text-slate-500 dark:text-neutral-400 leading-relaxed">
                {t(card.descKey, card.defaultDesc)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
